import React, { useEffect, useRef } from 'react';
import { redirect, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { register, registerFederated } from '../redux/actions/user';

// callbacks from oauth2 and stripe are intercepted by Amplify even before this page is rendered
const debugLevel = process.env.REACT_APP_MDEBUG || 3;

export default function CallbackHandler() {
  const { t, i18n } = useTranslation('site', { keyPrefix: 'register' });
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const registration = useSelector((state) => state.user.registration);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const selectToursSeen = useSelector((state) => state.user.profile.attributes?.toursSeen);
  const status = registration?.status;
  const username = registration?.username;

  const queryParams = new URLSearchParams(location.search);
  const queryParamCode = queryParams.get('code');
  const queryParamState = queryParams.get('state');
  const queryParamSource = queryParams.get('source');
  const queryParamErrorDescription = queryParams.get('error_description');

  // to not display "we are setting up" during the oauth callback from Cognito, we are going to just render a div if both query params above are there
  const noInterstitial = queryParamCode && queryParamState;

  console.info('DEBUG', 'CallbackHandler: ', registration, 'query params:', Object.fromEntries(queryParams.entries()));

  useEffect(() => {
    if (status === 'awaitingEmailConfirmation') {
      if (debugLevel > 0) console.log(`CallbackHandler: handle redirect to '/confirm/${username}' -- confirm email after internal user creation`);
      redirect(`/${i18n.language}/confirm/${username}`);
      if (debugLevel > 2) console.log('CallbackHandler: Gonna try navigate now');
      navigate(`/${i18n.language}/confirm/${username}`);
    }
  }, [status]);

  // ---------------------------
  // HANDLE STRIPE REDIRECT
  // ---------------------------

  // condition: the callback URL has 'source=stripe' parameter

  const didIAlreadyRun = useRef(false);

  useEffect(() => {
    if (didIAlreadyRun.current) return;
    if (queryParamSource !== 'stripe') return;
    if (debugLevel > 2) console.info('CallbackHandler: detected Stripe callback with query params', Object.fromEntries(queryParams.entries()));
    didIAlreadyRun.current = true; // ensure this effect runs only once

    const setupIntentId = queryParams.get('setup_intent');
    const setupIntentClientSecret = queryParams.get('setup_intent_client_secret');
    const payLater = queryParams.get('pay_later');

    if (!payLater) {
      sessionStorage.setItem('setupIntent', JSON.stringify({ setupIntentId, setupIntentClientSecret }));
      if (debugLevel > 2) console.info('CallbackHandler: the user is paying now; setupIntent stored in session storage', { setupIntentId });
    } else {
      if (debugLevel > 2) console.info('CallbackHandler: pay_later mode detected; payLater: true is stored in session storage');
      sessionStorage.setItem('payLater', JSON.stringify('true'));
    }

    // kick off the Cognito registration flow
    console.info('CallbackHandler: initiating Cognito registration flow after Stripe callback');

    const user = JSON.parse(localStorage.getItem('signUpObject'));

    if (!user) {
      console.error('CallbackHandler: expected signUpObject.user in localStorage NOT FOUND');
      window.location.replace('/fallback?fallbackMessage=missingSignUpObject');
      return;
    }

    try {
      // stripe setup is completed, now create the Cognito user
      dispatch(register(user));
    } catch (err) {
      console.info('CallbackHandler: detected error while running register');
      window.location.replace(`/fallback?fallbackMessage=${err}`);
    }
  }, [dispatch, queryParamSource, location.search]);

  // ---------------------------------------------
  // HANDLE COGNITO REDIRECT (signInWithRedirect)
  // ---------------------------------------------

  // condition: error_description query param exists and contains a predefined string

  useEffect(() => {
    if (!queryParamErrorDescription) return;
    if (didIAlreadyRun.current) return;
    didIAlreadyRun.current = true; // ensure this effect runs only once

    // Stage I: the only way to get federated email is to cause and handle an error
    // get email from error message and dispatch it to redux store
    const expectedErrorString = 'PreSignUp failed with error Error while getting Cognito users: EMAIL:';

    if (queryParamErrorDescription && queryParamErrorDescription.includes(expectedErrorString)) {
      if (debugLevel > 2) console.info('CallbackHandler: signInWithRedirect_failure handler started for string:', queryParamErrorDescription);

      const email = queryParamErrorDescription.split(expectedErrorString)[1].slice(0, -2);
      if (debugLevel > 2) console.info('CallbackHandler: extracted federated email from error message', email);

      // handles the case when user has a native Cognito account, but tries to use a federated identity with a different account
      // federatedSignIn will fail, but we also need to discontinue the choreographed process triggered here
      // if there is no signUpObject, it means we are not in the registration process and the error is relevant
      const formObject = JSON.parse(localStorage.getItem('signUpObject'));
      if (!formObject) {
        if (debugLevel > 2) console.error('CallbackHandler: cognitoHostedUI_failure handler: no signUpObject, redirecting back to Login');
        window.location.replace('/login?unknownUser=true');
      } else {
        // registerFederated returns a promise
        dispatch(registerFederated(email)).then(
          (x) => {
            console.log('CallbackHandler: registerFederated executed successfully, result:', x);
          },
          (err) => {
            console.warn('CallbackHandler: caught error while running registerFederated', err);
            // redirect to fallback handler
            window.location.replace(`/fallback?fallbackMessage=${queryParamErrorDescription}`);
          },
        );
      }
    }
  }, [dispatch, queryParamErrorDescription]);

  // 240920 DEPRECATED - no longer needed for the flow - user should just proceed to login
  // Stage II: external account has been merged into the new internal account, redirect to PaymentSuccessful
  // const expectedErrorStringMerge = 'PreSignUp+failed+with+error+Error+while+linking+external+user+to+Cognito+user%3A+USERMERGED%3A';

  // ---------------------------
  // REDIRECT AFTER LOGIN
  // ---------------------------

  // TODO 251107 is this still in use?
  // wait for hubListener to report that Amplify is done signing user up and update isLoggedIn in state
  useEffect(() => {
    if (isLoggedIn) {
      if (debugLevel > 0) console.log("CallbackHandler: isLoggedIn is now true -- user is logged in, redirect to '/app'");
      // if there are any toursSeen, go directly to Dashboard
      if (selectToursSeen && selectToursSeen.length > 0) navigate(`/${i18n.language}/app/dashboard`);
      // otherwise go to Prep
      else navigate(`/${i18n.language}/app/getready`);
    }
  }, [isLoggedIn]);

  return (
    <div className="h-screen bg-white flex items-center justify-center">
      {noInterstitial ? <div /> : <div className="text-2xl font-bold text-gray-500 animate-pulse">{t('pleaseWaitWhileWeAreSettingThingsUp')}</div>}
    </div>
  );
}
